import React, { useMemo } from "react"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

export interface LabelProps {
  children?: React.ReactNode
  isLoading?: boolean
  htmlFor?: string
  className?: string
  isHidden?: boolean
  "data-testid"?: string
  variant?: "subtle" | "bold"
  icon?: IconName
}

export const Label = React.memo(
  ({
    children,
    className,
    isLoading,
    htmlFor,
    isHidden,
    icon,
    variant = "subtle",
    ...props
  }: LabelProps) => {
    const resolvedClassName = classNames(
      className,
      "label",
      `label--${variant}`,
      isLoading ? "loading" : undefined,
      isHidden ? "visually-hidden" : undefined
    )

    const iconComponent = useMemo(() => {
      if (!icon) return null
      return <Icon size={IconSize.XS} name={icon} data-testid="label-icon" />
    }, [icon])

    if (isLoading) {
      return <Skeleton {...props} containerClassName={resolvedClassName} />
    }
    if (!children) {
      return null
    }

    if (!htmlFor) {
      return (
        <div {...props} className={resolvedClassName}>
          {children}
          {iconComponent}
        </div>
      )
    }

    return (
      <label {...props} className={resolvedClassName} htmlFor={htmlFor}>
        {children}
        {iconComponent}
      </label>
    )
  }
)
